const DEFAULT_COOKIE_PATH = "/";
const DEFAULT_COOKIE_SAME_SITE = "Lax";
const DEFAULT_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const COOKIE_CONSENT_KEY = "@HubNogueira:cookieConsent";
export const COOKIE_CONSENT_ESSENTIAL = "essential";
export const COOKIE_CONSENT_ALL = "all";

interface CookieOptions {
  path?: string;
  maxAge?: number;
  sameSite?: "Lax" | "Strict" | "None";
  secure?: boolean;
}

const isBrowser = typeof document !== "undefined";

const getCookieAttributes = (options: CookieOptions = {}) => {
  const path = options.path ?? DEFAULT_COOKIE_PATH;
  const maxAge = options.maxAge ?? DEFAULT_COOKIE_MAX_AGE;
  const sameSite = options.sameSite ?? DEFAULT_COOKIE_SAME_SITE;
  const secure =
    options.secure ??
    (typeof window !== "undefined" && window.location.protocol === "https:");

  return `path=${path}; max-age=${maxAge}; samesite=${sameSite}${secure ? "; secure" : ""}`;
};

export const setCookie = (
  name: string,
  value: string,
  options?: CookieOptions,
) => {
  if (!isBrowser) {
    return;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${getCookieAttributes(options)}`;
};

export const getCookie = (name: string) => {
  if (!isBrowser) {
    return null;
  }

  const target = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const matchedCookie = cookies.find((cookie) => cookie.startsWith(target));

  if (!matchedCookie) {
    return null;
  }

  return decodeURIComponent(matchedCookie.slice(target.length));
};

export const deleteCookie = (name: string, options?: CookieOptions) => {
  if (!isBrowser) {
    return;
  }

  document.cookie = `${encodeURIComponent(name)}=; ${getCookieAttributes({ ...options, maxAge: 0 })}`;
};

export const setJsonCookie = <T>(
  name: string,
  value: T,
  options?: CookieOptions,
) => {
  setCookie(name, JSON.stringify(value), options);
};

export const getJsonCookie = <T>(name: string): T | null => {
  const cookie = getCookie(name);

  if (!cookie) {
    return null;
  }

  try {
    return JSON.parse(cookie) as T;
  } catch {
    return null;
  }
};

export const hasCookieConsent = () => {
  const consentValue = getCookie(COOKIE_CONSENT_KEY);
  return (
    consentValue === COOKIE_CONSENT_ESSENTIAL ||
    consentValue === COOKIE_CONSENT_ALL
  );
};

export const hasEssentialCookieConsent = () => {
  return hasCookieConsent();
};

export const hasOptionalCookieConsent = () => {
  return getCookie(COOKIE_CONSENT_KEY) === COOKIE_CONSENT_ALL;
};
